import { useState, type Dispatch } from 'react';
import { ChevronRight } from 'lucide-react';
import { SectionCard } from '../../anketa/components/SectionCard';
import { SectionHeader } from '../../anketa/components/SectionHeader';
import { FurnitureTypePicker } from '../components/FurnitureTypePicker';
import type { QuoteAction } from '../quoteReducer';
import type { QuoteState } from '../types';

/**
 * Block 12 PR-D-A Phase D2 Step 3 — секция «Тип мебели».
 *
 * Trigger-button (текущий выбор либо placeholder) + bottom-sheet picker
 * с 10 enum-опциями + custom input. Прототип
 * `docs/prototypes/screen-2.12.10-cp.html` lines 130-144 + 377-441.
 *
 * Локальный state — `pickerOpen` (UI-only, не нужен в reducer).
 * Доменный state — `state.furnitureType` (через dispatch SET_FURNITURE_TYPE).
 */
export interface SectionFurnitureTypeProps {
  state: QuoteState;
  dispatch: Dispatch<QuoteAction>;
}

export function SectionFurnitureType({ state, dispatch }: SectionFurnitureTypeProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const current = state.furnitureType;
  const placeholder = 'Выбери тип мебели или укажи свой';

  return (
    <SectionCard testId="section-furniture-type">
      <SectionHeader>Тип мебели</SectionHeader>
      <button
        type="button"
        aria-label="Открыть выбор типа мебели"
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
        onClick={() => setPickerOpen(true)}
        className={`w-full min-h-14 px-4 rounded-xl bg-card border-[1.5px] border-accent flex items-center justify-between text-base font-semibold focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none ${
          current ? 'text-ink' : 'text-muted'
        }`}
        data-testid="furniture-type-trigger"
      >
        <span className="truncate text-left">{current ?? placeholder}</span>
        <ChevronRight size={20} className="text-muted flex-shrink-0" aria-hidden="true" />
      </button>

      <FurnitureTypePicker
        isOpen={pickerOpen}
        value={current}
        onChange={(next) => dispatch({ type: 'SET_FURNITURE_TYPE', value: next })}
        onClose={() => setPickerOpen(false)}
      />
    </SectionCard>
  );
}
